import { useEffect, useState } from 'react';
import { api } from './api';

export const DEFAULT_SITE_SETTINGS = {
    platform_name: null,
    company_name: null,
    contact_email: 'support@clickmeniu.ro',
    contact_phone: null,
    address: null,
    city: 'București',
    country: 'România',
    cui: null,
    trade_register: null,
    footer_text: null,
    logo_url: null,
    anpc_image_url: null,
    anpc_link: 'https://anpc.ro/ce-este-sal/',
    anpc_html: null,
    payment_banner_url: null,
    sol_link: 'https://ec.europa.eu/consumers/odr',
    facebook_url: null,
    instagram_url: null,
    tiktok_url: null,
    linkedin_url: null,
    youtube_url: null,
    trial_days: 14,
};

/** Denumire platformă din setări — null dacă lipsește (Logo folosește branding implicit). */
export function getPlatformDisplayName(settings) {
    const name = settings?.platform_name;
    if (typeof name === 'string' && name.trim()) return name.trim();
    return null;
}

/** Linie copyright footer — fără duplicare cu text footer din setări. */
export function formatFooterCopyrightLine(site) {
    const year = new Date().getFullYear();
    const platform = getPlatformDisplayName(site) || 'ClickMeniu.ro';
    const copyright = `© ${year} ${platform}`;
    const tagline = site?.footer_text?.trim();
    if (!tagline) return copyright;
    if (/^©\s*\d{4}/i.test(tagline)) return tagline;
    return `${copyright}. ${tagline}`;
}

export function formatSiteAddress(settings) {
    if (!settings) return '';
    const parts = [settings.address, settings.city, settings.country].filter(Boolean);
    return parts.join(', ');
}

// ---- Shared module-level cache + pub/sub so all mounted hooks get the update
// when super-admin saves. Previously each component fetched independently AND
// invalidate was a no-op — so admin changes (logo, social, etc.) wouldn't appear
// without a full page reload.
let _cache = null;
let _inflight = null;
const _subscribers = new Set();

async function fetchOnce(force = false) {
    if (_cache && !force) return _cache;
    if (_inflight) return _inflight;
    _inflight = api.get('/public/site-settings')
        .then((r) => {
            _cache = { ...DEFAULT_SITE_SETTINGS, ...(r.data || {}) };
            _inflight = null;
            _subscribers.forEach((cb) => { try { cb(_cache); } catch {} });
            return _cache;
        })
        .catch(() => {
            _inflight = null;
            return _cache || DEFAULT_SITE_SETTINGS;
        });
    return _inflight;
}

export function useSiteSettings() {
    const [settings, setSettings] = useState(_cache || DEFAULT_SITE_SETTINGS);
    const [loading, setLoading] = useState(!_cache);

    useEffect(() => {
        let active = true;
        const subscriber = (next) => { if (active) setSettings(next); };
        _subscribers.add(subscriber);
        fetchOnce().then((data) => {
            if (!active) return;
            setSettings(data);
            setLoading(false);
        });
        return () => {
            active = false;
            _subscribers.delete(subscriber);
        };
    }, []);

    return { settings, loading, platformName: getPlatformDisplayName(settings) };
}

/** Force refetch + notify ALL mounted hooks. Called after super-admin saves. */
export function invalidateSiteSettingsCache() {
    _cache = null;
    fetchOnce(true);  // refetch + broadcast to subscribers
}
